import {
  buildSmmOAuthStubNavigateTarget,
  isSocialMediaOAuthRedirectStubPathname,
  normalizeHashPathnameForRouting,
} from './decodeHashPathForRouting';

export const SMM_PENDING_OAUTH_STORAGE_KEY = 'dspSmmPendingOAuthReturn';
export const SMM_ADD_ACCOUNT_SUCCESS_PATH = '/social-media-monitoring/add-account-success';
export const SMM_MONITORED_INFO_SECTION = 'social-Media-Accounts';

export type SmmPendingOAuthReturn = {
  network?: string;
  primary?: string;
  accountId?: string;
};

export function persistSmmPendingOAuthReturn(context: SmmPendingOAuthReturn): void {
  if (typeof window === 'undefined' || !context.network) {
    return;
  }
  try {
    sessionStorage.setItem(SMM_PENDING_OAUTH_STORAGE_KEY, JSON.stringify(context));
  } catch {
    // sessionStorage may be unavailable
  }
}

export function readSmmPendingOAuthReturn(): SmmPendingOAuthReturn | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = sessionStorage.getItem(SMM_PENDING_OAUTH_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    return JSON.parse(raw) as SmmPendingOAuthReturn;
  } catch {
    return null;
  }
}

export function clearSmmPendingOAuthReturn(): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    sessionStorage.removeItem(SMM_PENDING_OAUTH_STORAGE_KEY);
  } catch {
    // ignore
  }
}

/** Pathname segment from the raw window hash (e.g. `#/_=_?primary=true` → `/_=_`). */
export function extractPathnameFromWindowHash(): string {
  if (typeof window === 'undefined') {
    return '';
  }
  const hash = window.location.hash || '';
  const withoutHash = hash.startsWith('#') ? hash.slice(1) : hash;
  const queryIndex = withoutHash.indexOf('?');
  const pathPart = queryIndex >= 0 ? withoutHash.slice(0, queryIndex) : withoutHash;
  return pathPart ? normalizeHashPathnameForRouting(pathPart) : '';
}

/** True when the window hash still reflects the Facebook/Instagram OAuth stub path. */
export function isOAuthStubInWindowHash(): boolean {
  return isSocialMediaOAuthRedirectStubPathname(extractPathnameFromWindowHash());
}

/** Query string from the raw window hash (e.g. `#/_=_?primary=true`). */
export function extractSearchFromWindowHash(): string {
  if (typeof window === 'undefined') {
    return '';
  }
  const hash = window.location.hash || '';
  const withoutHash = hash.startsWith('#') ? hash.slice(1) : hash;
  const queryIndex = withoutHash.indexOf('?');
  return queryIndex >= 0 ? withoutHash.slice(queryIndex) : '';
}

function mergePendingOAuthIntoParams(params: URLSearchParams): void {
  const pending = readSmmPendingOAuthReturn();
  if (!pending) {
    return;
  }
  if (pending.network && !params.has('network')) {
    params.set('network', pending.network);
  }
  if (pending.primary && !params.has('primary')) {
    params.set('primary', pending.primary);
  }
  if (pending.accountId && !params.has('accountid')) {
    params.set('accountid', pending.accountId);
  }
}

/**
 * When the extspa shell returns `#/_=?primary=…&username=…` without `redirectUrl`, infer the
 * add-account-success route from surviving SMM query params and sessionStorage (network).
 */
export function buildSmmAddAccountSuccessFallbackFromOAuthStubSearch(search: string): string | null {
  const query = !search ? '' : search.startsWith('?') ? search : `?${search}`;
  const params = new URLSearchParams(query);

  const hasUrlSmmReturnParams =
    params.has('primary') || params.has('username') || params.has('network') || params.has('accountid');

  if (!hasUrlSmmReturnParams) {
    return null;
  }

  mergePendingOAuthIntoParams(params);

  const serialized = params.toString();
  return serialized ? `${SMM_ADD_ACCOUNT_SUCCESS_PATH}?${serialized}` : SMM_ADD_ACCOUNT_SUCCESS_PATH;
}

/** Prefer explicit redirectUrl; otherwise infer add-account-success from stub params. */
export function resolveSmmOAuthStubNavigateTarget(search: string): string | null {
  return buildSmmOAuthStubNavigateTarget(search) ?? buildSmmAddAccountSuccessFallbackFromOAuthStubSearch(search);
}

/** Monitored Info deep link for the member who started SMM OAuth (GUTS-7084 cancel return). */
export function buildSmmMonitoredInfoReturnPath(accountId: string): string {
  const trimmedAccountId = accountId.trim();
  if (!trimmedAccountId) {
    return '';
  }
  return `/monitoring/${SMM_MONITORED_INFO_SECTION}/${trimmedAccountId}`;
}

/**
 * When OAuth is cancelled, the shell may return stub `/_=_` without redirectUrl.
 * Use session-persisted accountId to land on Monitored Info instead of Dashboard.
 */
export function resolveSmmOAuthCancelReturnTarget(): string | null {
  const accountId = readSmmPendingOAuthReturn()?.accountId?.trim();
  if (!accountId) {
    return null;
  }
  const path = buildSmmMonitoredInfoReturnPath(accountId);
  return path || null;
}
